import type { z } from 'zod'
import { env, IS_DEV } from '@/lib/env'
import { TRACEPARENT_HEADER, newTraceparent } from '@/lib/traceparent'
import { ProblemSchema } from '@/lib/vision/schemas'
import {
  AddBcSalesOrderLinesResponseSchema,
  AssignBcLotsResponseSchema,
  BcLotReadinessResponseSchema,
  BcSalesOrderFromPoResponseSchema,
  type AddBcSalesOrderLinesResponse,
  type AssignBcLotsResponse,
  type BcLotReadinessResponse,
  type BcSalesOrderFromPoResponse,
} from './schemas'

/**
 * Pulse-side Business Central Sales Order calls. Goes to Vision with the Entra
 * token; Vision mints `aud=heimdall` and forwards the bytes to Heimdall
 * untouched. Do not call Heimdall directly — the browser holds no
 * Heimdall-audience token, and pulse-v2 carries no Heimdall origin.
 */

export type TokenGetter = () => Promise<string>

export class HeimdallApiError extends Error {
  readonly status: number
  readonly problem: unknown

  constructor(status: number, message: string, problem: unknown) {
    super(message)
    this.name = 'HeimdallApiError'
    this.status = status
    this.problem = problem
  }
}

export interface BcSalesOrderRequestOptions {
  /** Vision origin. Defaults to `VITE_VISION_BASE_URL`. */
  baseUrl?: string
  getToken: TokenGetter
}

/**
 * Create Business Central Sales Order header(s) for a Vinculum SO
 * (`POST /v1/bc/sales-orders/from-po`). Headers only — call
 * {@link addBcSalesOrderLinesFromPo} afterwards to push the packed lines.
 *
 * `externalPoNumber` is what Heimdall matches on. Pass a single SO's external
 * document number including the `-N` suffix (`5KZZMABE-1`) for an exact
 * single-SO match; the bare PO (`5KZZMABE`) would fan out to every SO on it.
 *
 * `poLocation` is the PO's Amazon ship-to FC (e.g. `DED3`), which Heimdall
 * needs to resolve the MSD ship-to mapping. It is hard-required: Heimdall
 * answers a blank one with a per-SO `FAILED`, so this rejects up-front rather
 * than spending a round-trip to be told the same thing.
 *
 * Heimdall never throws on a per-SO failure. A 200 carries per-SO `status`
 * entries (`CREATED` / `DRY_RUN` / `SKIPPED` / `FAILED`) that callers must
 * inspect — see `summarizeBcSoResults`.
 */
export async function createBcSalesOrderFromPo(
  externalPoNumber: string,
  poLocation: string,
  opts: BcSalesOrderRequestOptions,
): Promise<BcSalesOrderFromPoResponse> {
  const po = externalPoNumber.trim()
  if (!po) {
    throw new HeimdallApiError(400, 'Cannot create a BC Sales Order without a PO number.', null)
  }
  const location = poLocation.trim()
  if (!location) {
    throw new HeimdallApiError(
      400,
      'Cannot create a BC Sales Order without a ship-to location (Amazon FC, e.g. DED3).',
      null,
    )
  }

  // dryRun MUST be explicit: Heimdall defaults it to true, so omitting it
  // returns a successful-looking preview that creates nothing in BC.
  return post('/v1/bc/sales-orders/from-po', BcSalesOrderFromPoResponseSchema, opts, {
    externalPoNumber: po,
    poLocation: location,
    dryRun: false,
  })
}

/**
 * Add the packed lines to the Sales Order header(s) created for a Vinculum SO
 * (`POST /v1/bc/sales-orders/from-po/lines`). Heimdall reads each order's
 * persisted packed lines, skips lines BC already has, and reports per line.
 *
 * Takes no `poLocation` — the header call already stamped the location onto
 * the BC Sales Order, and Heimdall looks the header up by
 * `externalDocumentNo`.
 *
 * Same per-SO contract as {@link createBcSalesOrderFromPo}: a 200 can still
 * carry `FAILED` or `NO_PACKED_LINES` entries.
 */
export async function addBcSalesOrderLinesFromPo(
  externalPoNumber: string,
  opts: BcSalesOrderRequestOptions,
): Promise<AddBcSalesOrderLinesResponse> {
  const po = externalPoNumber.trim()
  if (!po) {
    throw new HeimdallApiError(400, 'Cannot add BC Sales Order lines without a PO number.', null)
  }

  return post('/v1/bc/sales-orders/from-po/lines', AddBcSalesOrderLinesResponseSchema, opts, {
    externalPoNumber: po,
    dryRun: false,
  })
}

/**
 * Whether each Sales Order under a PO is ready for lot assignment
 * (`GET /v1/bc/sales-orders/from-po/lots/readiness`).
 *
 * Read-only — it reads BC live to see whether it holds lines for the Sales
 * Order, and folds that in with the lot state Heimdall remembers. Deliberately
 * takes the **bare PO** (`5KZZMABE`), not a single SO's `{PO}-{seq}`: one call
 * answers for every SO on the PO, and the panel picks each cell's row out of
 * `results` by `externalDocumentNo`. Firing this per wave would be one request
 * per row for the same answer.
 *
 * `readiness` comes back as a bare string. See `BcLotReadinessResultSchema` for
 * why, and `bcLotState` for the mapping.
 */
export async function getBcLotReadiness(
  externalPoNumber: string,
  opts: BcSalesOrderRequestOptions,
): Promise<BcLotReadinessResponse> {
  const po = externalPoNumber.trim()
  if (!po) {
    throw new HeimdallApiError(400, 'Cannot read lot readiness without a PO number.', null)
  }

  return get(
    `/v1/bc/sales-orders/from-po/lots/readiness?externalPoNumber=${encodeURIComponent(po)}`,
    BcLotReadinessResponseSchema,
    opts,
  )
}

/**
 * Assign lots to the Sales Order's lines
 * (`POST /v1/bc/sales-orders/from-po/lots`). Heimdall picks the lots in BC by
 * expiry and writes the reservation entries, then echoes what it picked.
 *
 * Takes no `poLocation` — the header call already stamped the location onto the
 * BC Sales Order — and requires the lines to be on it, which is why the panel
 * only offers this once the line state reads `present`.
 *
 * Same per-SO contract as the two calls above: Heimdall answers a per-SO
 * failure with a 200 carrying a `FAILED` row rather than an HTTP error, so
 * callers judge the outcome on the per-SO `status` — see
 * `summarizeBcLotResults`. Replaying it is safe; an SO whose lines already
 * carry tracking comes back `NOTHING_TO_DO`.
 */
export async function assignBcLotsFromPo(
  externalPoNumber: string,
  opts: BcSalesOrderRequestOptions,
): Promise<AssignBcLotsResponse> {
  const po = externalPoNumber.trim()
  if (!po) {
    throw new HeimdallApiError(400, 'Cannot assign BC lots without a PO number.', null)
  }

  // dryRun MUST be explicit: Heimdall defaults it to true, so omitting it
  // returns a successful-looking PLANNED preview that reserves nothing in BC.
  return post('/v1/bc/sales-orders/from-po/lots', AssignBcLotsResponseSchema, opts, {
    externalPoNumber: po,
    dryRun: false,
  })
}

async function get<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  opts: BcSalesOrderRequestOptions,
): Promise<z.infer<S>> {
  const baseUrl = (opts.baseUrl ?? env.VITE_VISION_BASE_URL).replace(/\/$/, '')
  const token = await opts.getToken()

  const res = await fetch(`${baseUrl}${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      [TRACEPARENT_HEADER]: newTraceparent(),
    },
    credentials: 'omit',
  })

  if (!res.ok) throw await toApiError(res)

  return parse(schema, await res.text(), `GET ${path}`)
}

async function post<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  opts: BcSalesOrderRequestOptions,
  body: Record<string, unknown>,
): Promise<z.infer<S>> {
  const baseUrl = (opts.baseUrl ?? env.VITE_VISION_BASE_URL).replace(/\/$/, '')
  const token = await opts.getToken()

  const res = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      [TRACEPARENT_HEADER]: newTraceparent(),
    },
    body: JSON.stringify(body),
    credentials: 'omit',
  })

  if (!res.ok) throw await toApiError(res)

  return parse(schema, await res.text(), `POST ${path}`)
}

function parse<S extends z.ZodTypeAny>(schema: S, text: string, route: string): z.infer<S> {
  const raw = text ? (JSON.parse(text) as unknown) : null
  const parsed = schema.safeParse(raw)
  if (!parsed.success) {
    if (IS_DEV) {
      console.warn(`[heimdall] response failed schema at ${route}`, parsed.error.flatten(), raw)
    }
    throw new HeimdallApiError(200, `Malformed response for ${route}`, parsed.error.flatten())
  }
  return parsed.data
}

async function toApiError(res: Response): Promise<HeimdallApiError> {
  const text = await res.text().catch(() => '')
  if (!text) {
    return new HeimdallApiError(res.status, `${res.status} ${res.statusText}`, null)
  }
  try {
    const raw = JSON.parse(text) as unknown
    const problem = ProblemSchema.safeParse(raw)
    if (problem.success) {
      return new HeimdallApiError(
        res.status,
        problem.data.detail || problem.data.title,
        problem.data,
      )
    }
    return new HeimdallApiError(res.status, `${res.status} ${res.statusText}`, raw)
  } catch {
    return new HeimdallApiError(res.status, text, null)
  }
}
